import { Controller } from "@hotwired/stimulus";
import {
  MIN_REMOTE_QUERY_LENGTH,
  NavigationRecordCache,
  REMOTE_SEARCH_DEBOUNCE_MS,
  commandGroupHeader,
  commandResultRow,
  escapeHtml,
  isCommandSearchShortcut,
  normalizeTransientNavigationState,
  cachedAdminRecords,
  platformModifierLabel,
  rankAdminCommandResults,
} from "../lib/admin_command_search.mjs";

const GROUP_LABELS = {
  page: "Pages",
  partner: "Partners",
  claim: "Claims",
  bill: "Bills",
  member: "Members",
  user: "Users",
};
// A record can arrive with nothing to show under its title (a claim with no partner,
// member, or service date yet), so the secondary line falls back per type.
const FALLBACK_META = { partner: "Open partner", claim: "Open claim", bill: "Open bill", member: "Open member", user: "Open user" };

// The admin ⌘K palette (docs/admin_command_search.md). Pages and partners ship with the
// page and match locally; claims, bills, members, and users come from GET /admin/command_search,
// which is internal-only and gates each type by the permission its destination page
// requires.
export default class extends Controller {
  static targets = ["overlay", "input", "results", "status", "modifier"];
  static values = {
    pages: Array,
    // Every partner (internal actors can open any partner page), so switching partners
    // from admin is a typed name or slug away. Matched only once something is typed.
    partners: { type: Array, default: [] },
    recordIcons: Object,
    searchUrl: String,
    // False for internal actors who can open no record type (claims and bills need
    // support, users need admin): the palette is pages-only and never calls the endpoint.
    recordsSearchable: { type: Boolean, default: true },
  };

  connect() {
    this.records = [];
    this.recordsQuery = "";
    this.visibleResults = [];
    this.selectedIndex = -1;
    this.searchOpen = false;
    this.loading = false;
    this.degraded = false;
    this.searchTimer = null;
    this.abortController = null;
    this.previouslyFocusedElement = null;
    // Case-sensitive: guarantor ids match exactly, so a miss on `grt-1` says nothing
    // about `GRT-1`.
    this.cache = new NavigationRecordCache(undefined, { caseSensitive: true });

    const platform = navigator.userAgentData?.platform || navigator.platform || "";
    const modifier = platformModifierLabel(platform);
    this.modifierTargets.forEach((target) => { target.textContent = modifier; });
  }

  disconnect() {
    this.prepareForCache();
  }

  // This palette lives in the admin navbar (admin/_navbar), the only navbar on admin
  // pages, so nothing else binds ⌘K here. The guard is belt and braces: if a page ever
  // renders two palettes, only the first to see the event acts on it.
  handleGlobalKeydown(event) {
    if (isCommandSearchShortcut(event)) {
      if (event.defaultPrevented) return;

      event.preventDefault();
      this.searchOpen ? this.closeSearch() : this.openSearch();
      return;
    }

    if (event.key !== "Escape" || !this.searchOpen) return;

    event.preventDefault();
    this.closeSearch();
  }

  openSearch() {
    this.previouslyFocusedElement = document.activeElement;
    this.searchOpen = true;
    this.overlayTarget.hidden = false;
    this.previousBodyOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    this.inputTarget.value = "";
    this.records = [];
    this.recordsQuery = "";
    this.loading = false;
    this.degraded = false;
    this.renderResults();
    window.requestAnimationFrame(() => this.inputTarget.focus());
  }

  closeSearch() {
    this.searchOpen = false;
    this.overlayTarget.hidden = true;
    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    this.restoreBodyScroll();
    if (this.previouslyFocusedElement?.isConnected) this.previouslyFocusedElement.focus();
    this.previouslyFocusedElement = null;
  }

  prepareForCache() {
    window.clearTimeout(this.searchTimer);
    this.abortController?.abort();
    normalizeTransientNavigationState({
      overlay: this.hasOverlayTarget ? this.overlayTarget : null,
      body: document.body,
      previousBodyOverflow: this.previousBodyOverflow,
    });
    this.searchOpen = false;
    this.loading = false;
    this.searchTimer = null;
    this.abortController = null;
    this.previousBodyOverflow = undefined;
    this.previouslyFocusedElement = null;
    // Rendered rows carry patient/member names and emails; keep them out of Turbo's
    // page snapshot.
    if (this.hasResultsTarget) this.resultsTarget.innerHTML = "";
    if (this.hasStatusTarget) this.statusTarget.innerHTML = "";
    if (this.hasInputTarget) this.inputTarget.value = "";
    this.visibleResults = [];
  }

  closeFromBackdrop(event) {
    if (event.target === this.overlayTarget) this.closeSearch();
  }

  search() {
    window.clearTimeout(this.searchTimer);
    const query = this.query();

    if (query.length < MIN_REMOTE_QUERY_LENGTH || !this.recordsSearchableValue) {
      this.abortController?.abort();
      this.degraded = false;
      this.showRecords([], query);
      return;
    }

    // Every payload the endpoint returned stays cached for the life of the page, so
    // backspacing and retyping cost no request. Unlike the partner palette there is no
    // empty-prefix short circuit: see cachedAdminRecords.
    const cached = cachedAdminRecords(this.cache, query);
    if (cached) {
      this.abortController?.abort();
      // Timed-out payloads are never cached, so a cache hit is never degraded.
      this.degraded = false;
      this.showRecords(cached, query);
      return;
    }

    // Keep the records already on screen while the request is in flight, but only the
    // ones that still match what is typed (recordsAreCurrent below), so the list does not
    // blank out mid-typing and cannot leave an unrelated record selected either.
    this.loading = true;
    this.renderResults();
    this.searchTimer = window.setTimeout(() => this.fetchRemoteResults(query), REMOTE_SEARCH_DEBOUNCE_MS);
  }

  handleInputKeydown(event) {
    if (!["ArrowDown", "ArrowUp", "Enter"].includes(event.key)) return;

    event.preventDefault();
    if (!this.visibleResults.length) return;

    if (event.key === "ArrowDown") {
      this.selectIndex((this.selectedIndex + 1) % this.visibleResults.length);
    } else if (event.key === "ArrowUp") {
      const previous = this.selectedIndex <= 0 ? this.visibleResults.length - 1 : this.selectedIndex - 1;
      this.selectIndex(previous);
    } else {
      this.activateResult(this.visibleResults[Math.max(this.selectedIndex, 0)], event.metaKey || event.ctrlKey);
    }
  }

  selectFromPointer(event) {
    const result = event.target.closest("[data-result-index]");
    if (!result) return;

    this.selectIndex(Number(result.dataset.resultIndex), false);
  }

  activateFromPointer(event) {
    const result = event.target.closest("[data-result-index]");
    if (!result) return;

    event.preventDefault();
    this.activateResult(this.visibleResults[Number(result.dataset.resultIndex)], event.metaKey || event.ctrlKey);
  }

  async fetchRemoteResults(query) {
    // One request in flight at a time: a keystroke abandons the previous one rather than
    // letting two answers race for the same list.
    this.abortController?.abort();
    this.abortController = new AbortController();
    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", query);

    try {
      const response = await fetch(url, {
        credentials: "same-origin",
        headers: { Accept: "application/json", "X-Requested-With": "XMLHttpRequest" },
        signal: this.abortController.signal,
      });
      if (!response.ok) throw new Error(`Admin command search failed with ${response.status}`);

      const payload = await response.json();
      const records = [...(payload.claims || []), ...(payload.bills || []), ...(payload.members || []), ...(payload.users || [])];
      // A timed-out search returns a partial (often empty) payload, so it must not be
      // cached as the answer for this query or every longer query built on it.
      if (payload.timed_out) {
        if (query !== this.query()) return;

        this.degraded = true;
        this.showRecords(records, query);
        return;
      }

      this.cache.set(query, records);
      if (query !== this.query()) return;

      this.degraded = false;
      this.showRecords(records, query);
    } catch (error) {
      if (error.name === "AbortError") return;
      // An older request can fail while a newer query is mid-debounce; its failure says
      // nothing about what is typed now.
      if (query !== this.query()) return;

      // Drop the records so the error status is not sitting above results that no longer
      // correspond to what was typed, but keep rendering the page shortcuts the status
      // says still work.
      this.showRecords([], query);
      this.renderStatus(`
        <div class="text-sm font-semibold text-support-dark-800">Record search is unavailable</div>
        <div class="mt-1 text-xs text-support-dark-450">Page shortcuts still work.</div>
      `);
    }
  }

  showRecords(records, query) {
    this.records = records;
    this.recordsQuery = query;
    this.loading = false;
    this.renderResults();
  }

  renderResults() {
    const query = this.query().toLowerCase();
    this.visibleResults = rankAdminCommandResults(
      [...this.pagesValue, ...this.partnersValue],
      this.records,
      query,
      { recordsAreCurrent: this.recordsQuery === this.query() }
    );

    let lastType = null;
    this.resultsTarget.innerHTML = this.visibleResults.map((result, index) => {
      const header = result.type === lastType ? "" : commandGroupHeader(GROUP_LABELS[result.type] || "Results");
      lastType = result.type;
      return header + commandResultRow({
        result,
        index,
        query,
        idPrefix: "admin-command-result",
        iconHtml: this.recordIconsValue[result.type],
        fallbackMeta: FALLBACK_META[result.type] || "Open page",
      });
    }).join("");

    if (this.visibleResults.length) {
      this.statusTarget.classList.add("hidden");
      this.selectIndex(0, false);
    } else {
      this.selectedIndex = -1;
      this.inputTarget.removeAttribute("aria-activedescendant");
      this.renderStatus(this.loading ? this.loadingState() : this.emptyState(this.query()));
    }
  }

  loadingState() {
    return `<div class="text-sm text-support-dark-450">Searching claims, bills, members, and users…</div>`;
  }

  emptyState(query) {
    if (this.degraded) {
      return `<span class="mb-2.5 flex justify-center text-support-dark-500">${this.recordIconsValue.empty || ""}</span>
        <div class="text-sm font-semibold text-support-dark-800">Record search timed out</div>
        <div class="mt-1 text-xs text-support-dark-450">Try a more specific query, or an exact ID. Page shortcuts still work.</div>`;
    }

    if (!this.recordsSearchableValue) {
      return `<span class="mb-2.5 flex justify-center text-support-dark-500">${this.recordIconsValue.empty || ""}</span>
        <div class="text-sm font-semibold text-support-dark-800">${query ? `No pages match “${escapeHtml(query)}”` : "Search admin pages"}</div>`;
    }

    const guidance = query.length >= MIN_REMOTE_QUERY_LENGTH
      ? `<div class="text-sm font-semibold text-support-dark-800">No matches for “${escapeHtml(query)}”</div>
         <div class="mt-1 text-xs text-support-dark-450">Search by admin page, claim ID, internal ID, patient or member name, email, or guarantor ID.</div>`
      : `<div class="text-sm font-semibold text-support-dark-800">Keep typing to search records</div>
         <div class="mt-1 text-xs text-support-dark-450">2+ characters finds claims, bills, members, and users.</div>`;

    return `<span class="mb-2.5 flex justify-center text-support-dark-500">${this.recordIconsValue.empty || ""}</span>${guidance}`;
  }

  renderStatus(html) {
    this.statusTarget.innerHTML = html;
    this.statusTarget.classList.remove("hidden");
  }

  selectIndex(index, shouldScroll = true) {
    this.selectedIndex = index;
    this.resultsTarget.querySelectorAll("[data-result-index]").forEach((element, elementIndex) => {
      const selected = elementIndex === index;
      element.setAttribute("aria-selected", String(selected));
      element.classList.toggle("bg-complementary-purple-200", selected);

      const chip = element.querySelector("[data-icon-chip]");
      chip?.classList.toggle("bg-support-light-250", !selected);
      chip?.classList.toggle("text-support-dark-600", !selected);
      chip?.classList.toggle("bg-white", selected);
      chip?.classList.toggle("text-core-purple-700", selected);

      const enterHint = element.querySelector("[data-enter-hint]");
      enterHint?.classList.toggle("hidden", !selected);
      enterHint?.classList.toggle("flex", selected);

      if (selected && shouldScroll) element.scrollIntoView({ block: "nearest" });
    });

    if (index >= 0) this.inputTarget.setAttribute("aria-activedescendant", `admin-command-result-${index}`);
  }

  activateResult(result, newTab) {
    if (!result?.url) return;

    if (newTab) {
      window.open(result.url, "_blank", "noopener,noreferrer");
      return;
    }

    this.prepareForCache();
    if (window.Turbo?.visit) {
      window.Turbo.visit(result.url);
    } else {
      window.location.assign(result.url);
    }
  }

  query() {
    return this.hasInputTarget ? this.inputTarget.value.trim() : "";
  }

  restoreBodyScroll() {
    if (this.previousBodyOverflow === undefined) return;

    document.body.style.overflow = this.previousBodyOverflow;
    this.previousBodyOverflow = undefined;
  }
}
